import React from 'react';
import PropTypes from 'prop-types';
import { arrayMove, SortableContext, sortableKeyboardCoordinates, useSortable, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { closestCenter, DndContext, KeyboardSensor, PointerSensor, useSensor, useSensors } from '@dnd-kit/core';
import {CSS as dndCSS} from '@dnd-kit/utilities';

import { Button, HEADER_BUTTON_DARK_BLUE } from '../../components/PageHeader/Button';
import Strings from '../../Strings';
import './index.scss';
import { OverlayScrollbarsComponent } from 'overlayscrollbars-react';

function DropdownItems(props) {
  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  const handleChange = (item, value) => {
    props.handleChange(item, value);
  };
  const handleGroupChange = (group, value) => {
    props.items
      .filter(i => i.group === group)
      .forEach(item => {
        props.handleChange(item.id, value);
      });
  };
  
  function handleDragEnd(event) {
    const {active, over} = event;
    
    if (active.id !== over.id) {
      const oldIndex = props.items.findIndex(i => i.id === active.id);
      const newIndex = props.items.findIndex(i => i.id === over.id);
      
      props.advancedFeatures?.changeColumnOrder(oldIndex, newIndex);
    }
  }

  const { topLabelText } = props;

  // Group items by "group" property
  const groupedItems = {};
  props.items.forEach(item => {
    const group = item.group || 'none';
    if (!groupedItems[group]) {
      groupedItems[group] = [];
    }
    groupedItems[group].push(item);
  });

  return (
    <div className="dropdown">
        <div className="top-label">
          <label className="top-label">{topLabelText ? topLabelText : Strings.showSelectedItems}</label>
        </div>
        <OverlayScrollbarsComponent
          defer
          options={{ scrollbars: { autoHide: 'leave', autoHideDelay: '100' } }}
          className="scrollbar-right-margin-min"
        >
          {Object.entries(groupedItems).map(([group, items]) => (
          <React.Fragment key={group}>
            {group !== 'none' && (
              <Item
                key={`group_${group}`}
                id={group}
                label={group}
                checked={!items.some(i => !i.checked)}
                handleChange={handleGroupChange}
                disabled={!items.some(i => !i.disabled)}
                grouped={false}
                advancedFeatures={props.advancedFeatures}
              />
            )}
            <DndContext
              sensors={sensors}
              collisionDetection={closestCenter}
              onDragEnd={handleDragEnd}
            >
              <SortableContext
                items={items}
                strategy={verticalListSortingStrategy}
              >
                {items.map((item, i) => (
                  <Item
                    key={item.id}
                    id={item.id}
                    label={item.label}
                    checked={item.checked}
                    handleChange={handleChange}
                    disabled={item.disabled || (i === 0 && !props.dontDisableFirstEntry)}
                    grouped={group !== 'none'}
                    advancedFeatures={props.advancedFeatures}
                  />
                ))}
              </SortableContext>
            </DndContext>
          </React.Fragment>
        ))}
        </OverlayScrollbarsComponent>

        <div className="reset-button">
          <Button class={HEADER_BUTTON_DARK_BLUE} onClick={props.resetToDefaults} title={Strings.resetToDefaults} />
        </div>
    </div>
  );
}

DropdownItems.propTypes = {
  states: PropTypes.object, // states of switches
  items: PropTypes.array, // switches
  handleChange: PropTypes.func,
  resetToDefaults: PropTypes.func,
  topLabelText: PropTypes.string,
  dontDisableFirstEntry: PropTypes.bool,
};

function Item(props) {
  const { handleChange, id, label, checked, disabled, grouped, advancedFeatures } = props;
  const { onUp, onDown, changeColumnPinning, pinningState } = advancedFeatures || {};
  const {
    attributes,
    listeners,
    setNodeRef,
    setActivatorNodeRef,
    transform,
    transition,
  } = useSortable({id});

  const style = {
    transform: dndCSS?.Transform?.toString(transform),
    transition,
  };

  const pinned = pinningState?.left?.includes(id);
  const onPin = (id) => {
    changeColumnPinning(id, pinned ? false : 'left');
  };

  return (
    <div
      align="left"
      className={`filter ${grouped ? 'grouped' : ''}`}
      ref={setNodeRef} style={style} {...attributes}
    >
      {onUp && onDown && (
        <button
          ref={setActivatorNodeRef}
          cursor="grab"
          className="drag-handle"
          {...listeners}
        />
      )}
      {changeColumnPinning && <button className={`arrow-button ${pinned ? 'pinned' : 'pin'}`} onClick={() => onPin(id)}/>}
      {onUp && <button className="arrow-button" onClick={() => onUp(id)}>⬆️</button>}
      {onDown && <button className="arrow-button" onClick={() => onDown(id)}>⬇️</button>}
      <label className="form-control" htmlFor={id}>
        <input
          type="checkbox"
          className="filter-label"
          id={id}
          onClick={e => handleChange(id, e.target.checked)}
          checked={checked}
          disabled={disabled}
        />
        <div className="filter-text">{label}</div>
      </label>
    </div>
  );
}
Item.PropTypes = {
  id: PropTypes.string,
  handleChange: PropTypes.func,
  label: PropTypes.any,
  checked: PropTypes.bool,
  disabled: PropTypes.bool,
  grouped: PropTypes.bool,
};

class DropdownTableFilter extends React.PureComponent {
  constructor(props) {
    super(props);

    this.state = { open: false };

    this.resetToDefaults = this.resetToDefaults.bind(this);
  }

  setOpen(value) {
    this.setState({ open: value });
  }

  resetToDefaults() {
    this.setOpen(false);
    this.props.resetToDefaults();
  }

  onBlur = event => {
    // if Click is outside of this list components, lets close it, mean that we lost focus
    if (!event.currentTarget.contains(event.relatedTarget)) {
      this.setOpen(false);
    }
  };

  render() {
    const { open } = this.state;
    const { topLabelText, advancedFeatures } = this.props;

    const dropdownMenu = (
      <React.Fragment>
        <DropdownItems
          items={this.props.filters}
          handleChange={this.props.handleChange}
          resetToDefaults={this.resetToDefaults}
          topLabelText={topLabelText}
          dontDisableFirstEntry={this.props.dontDisableFirstEntry || undefined}
          advancedFeatures={advancedFeatures}
        />
      </React.Fragment>
    );

    const iconStyle = open ? 'filtervisibility-icon-on' : 'filtervisibility-icon';

    return (
      <div className="dropdown-container" tabIndex={1} onBlur={e => this.onBlur(e)} style={this.props.containerWidth ? {left: this.props.containerWidth - 43} : {}}>
        <div className="filtervisibility-container">
          <div className={iconStyle} onClick={() => this.setOpen(!this.state.open)}></div>
        </div>
        {open && dropdownMenu}
      </div>
    );
  }

  static propTypes = {
    filters: PropTypes.array,
    handleChange: PropTypes.func,
    resetToDefaults: PropTypes.func,
    topLabelText: PropTypes.string,
    dontDisableFirstEntry: PropTypes.bool,
    containerWidth: PropTypes.number,
  };
}

class FiltersStateHelper {
  static changeFilterState = (filters, filter_id, value) => {
    const to_update = filters.find(e => e.id === filter_id);
    to_update.checked = value;
    return filters.map(filter => (filter.filter_id !== filter_id ? filter : to_update));
  };

  static getExcludedItems = filters_state => {
    const excluded_items = filters_state.map(e => {
      if (e.checked === false) return e.id;
      return null;
    });

    return excluded_items.filter(e => e !== null);
  };

  static generateDefaultFilterStateFromColumns = columns => {
    return columns.map(e => {
      return {
        id: e.key,
        label: e.props.title,
        checked: true,
      };
    });
  };

  static getDefaultOnFilters = filters => {
    /* eslint-disable */
    filters.forEach((filter, index, filters_array) => {
      filters_array[index].checked = true;
    });
    /* eslint-enable */
    return filters;
  };
}

export { FiltersStateHelper, DropdownTableFilter };
